import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Bell, 
  Cloud, 
  FileCheck, 
  RefreshCw, 
  Users, 
  Brain, 
  AlertTriangle, 
  Database, 
  GraduationCap, 
  ArrowRight,
  CheckCircle
} from 'lucide-react';

interface ServicesOverviewProps {
  setCurrentPage: (page: string) => void;
}

const ServicesOverview: React.FC<ServicesOverviewProps> = ({ setCurrentPage }) => {
  const services = [
    {
      icon: Shield,
      title: "Managed Security",
      description: "Comprehensive security management and monitoring services to protect your infrastructure 24/7.",
      features: [
        "Real-time threat monitoring",
        "Security infrastructure management",
        "Incident response and remediation",
        "Continuous security assessment"
      ],
      link: "managed-security"
    },
    {
      icon: Bell,
      title: "Security Operations Center (SOC)",
      description: "Dedicated security operations center providing round-the-clock monitoring and threat detection.",
      features: [
        "24/7 security monitoring",
        "Advanced threat detection",
        "Security event analysis",
        "Rapid incident response"
      ],
      link: "soc"
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      description: "Secure cloud infrastructure and applications with comprehensive protection strategies.",
      features: [
        "Cloud infrastructure security",
        "Application security",
        "Data protection",
        "Compliance management"
      ],
      link: "cloud-security"
    },
    {
      icon: FileCheck,
      title: "Compliance & Risk Management",
      description: "Ensure regulatory compliance and manage security risks effectively.",
      features: [
        "Regulatory compliance",
        "Risk assessment",
        "Policy development",
        "Audit support"
      ],
      link: "compliance"
    },
    {
      icon: RefreshCw,
      title: "Digital Transformation",
      description: "Transform and optimize your security processes for maximum efficiency.",
      features: [
        "Process optimization",
        "Security automation",
        "Digital strategy",
        "Technology integration"
      ],
      link: "transformation"
    },
    {
      icon: Users,
      title: "Industry Solutions",
      description: "Specialized security solutions tailored to your industry's unique requirements.",
      features: [
        "Industry-specific compliance",
        "Customized security controls",
        "Sector expertise",
        "Best practices"
      ],
      link: "specialized-industry"
    },
    {
      icon: Brain,
      title: "Threat Intelligence",
      description: "Advanced threat detection and intelligence services to stay ahead of cyber threats.",
      features: [
        "Threat monitoring",
        "Intelligence analysis",
        "Proactive defense",
        "Threat hunting"
      ],
      link: "threat-intelligence"
    },
    {
      icon: AlertTriangle,
      title: "Incident Response",
      description: "Swift and effective response to security incidents with minimal business impact.",
      features: [
        "Incident detection",
        "Response coordination",
        "Breach containment",
        "Recovery support"
      ],
      link: "incident-response"
    },
    {
      icon: Database,
      title: "Data Security",
      description: "Comprehensive data protection and breach prevention strategies.",
      features: [
        "Data encryption",
        "Access control",
        "Data governance",
        "Privacy protection"
      ],
      link: "data-security"
    },
    {
      icon: GraduationCap,
      title: "Training & Awareness",
      description: "Security education and awareness programs to strengthen your human firewall.",
      features: [
        "Security awareness training",
        "Phishing simulations",
        "Compliance training",
        "Role-based education"
      ],
      link: "training"
    }
  ];

  const handleServiceClick = (link: string) => {
    setCurrentPage(link);
  };

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Shield className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Our Services</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Comprehensive cybersecurity solutions to protect your organization from evolving digital threats
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 flex flex-col hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <service.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{service.description}</p>
              
              <ul className="space-y-3 mb-6 flex-grow">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleServiceClick(service.link)}
                className="w-full btn-primary mt-auto"
              >
                <span className="flex items-center justify-center">
                  Learn More
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-20 text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Enhance Your Security?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our security experts to discuss your organization's needs and discover the right solutions for you
            </p>
            <button 
              onClick={() => handleServiceClick('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesOverview;